/**
 * ChunkErrorBoundary - Handles dynamic import/chunk loading failures
 *
 * When a new deployment occurs, old chunk files are replaced with new ones.
 * Users with the app already loaded may reference old chunks that no longer exist,
 * resulting in "Failed to fetch dynamically imported module" errors.
 *
 * This boundary catches those errors and automatically refreshes the page.
 */

import {Component, type ErrorInfo, type ReactNode} from 'react'
import {reportError} from '../../../utils/errorReporter'

// Session storage key to prevent infinite refresh loops
const CHUNK_REFRESH_KEY = 'chunk_error_refresh'

interface Props {
  children: ReactNode
}

interface State {
  hasError: boolean
}

/**
 * Checks if an error is a chunk/dynamic import loading failure
 */
function isChunkLoadError(error: Error): boolean {
  const message = error.message?.toLowerCase() || ''
  const name = error.name?.toLowerCase() || ''

  return (
    message.includes('failed to fetch dynamically imported module') ||
    message.includes('loading chunk') ||
    message.includes('loading css chunk') ||
    message.includes('dynamically imported module') ||
    name === 'chunkerror' ||
    // Network errors that could be related to chunk loading
    (message.includes('failed to fetch') && message.includes('.js'))
  )
}

export class ChunkErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = {hasError: false}
  }

  static getDerivedStateFromError(): State {
    return {hasError: true}
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('ChunkErrorBoundary caught error:', error)
    console.error('Component stack:', errorInfo.componentStack)
    reportError('ErrorBoundary', `ChunkErrorBoundary: ${error.message}`, error.stack)

    // If it's a chunk loading error, auto-refresh (with loop protection)
    if (isChunkLoadError(error)) {
      const lastRefresh = sessionStorage.getItem(CHUNK_REFRESH_KEY)
      const now = Date.now()

      // Only auto-refresh if we haven't refreshed in the last 10 seconds
      // This prevents infinite refresh loops if there's a persistent issue
      if (!lastRefresh || now - parseInt(lastRefresh, 10) > 10000) {
        sessionStorage.setItem(CHUNK_REFRESH_KEY, now.toString())
        window.location.reload()
      }
    }
  }

  render(): ReactNode {
    // We don't show an error UI for chunk errors since we auto-refresh
    // This render only happens briefly before the refresh, or if refresh loop protection kicks in
    if (this.state.hasError) {
      return null
    }

    return this.props.children
  }
}

export default ChunkErrorBoundary
